import { Marked, type RendererThis, type Tokens } from 'marked';
import { allocateHeadingAnchorId } from './headingAnchorId';
import { extractMarkdownTableOfContents, type MarkdownTocEntry } from './markdownTableOfContents';

export interface SkillDescription {
    title: string;
    html: string;
    tableOfContents: MarkdownTocEntry[];
}

const HTML_ESCAPE_MAP: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (ch) => HTML_ESCAPE_MAP[ch]);
}

/** Exported for unit tests; used by custom link/image renderers. */
export function sanitizeUrl(href: string): string {
    const stripped = href.replace(/[\x00-\x1f\x7f]/g, '').trim();
    if (/^javascript:/i.test(stripped)) {
        return '';
    }
    if (/^data:/i.test(stripped)) {
        return '';
    }
    return escapeHtml(stripped);
}

function buildTitleAttr(title: string | null | undefined): string {
    return title ? ` title="${escapeHtml(title)}"` : '';
}

const LINK_ICON_SVG = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"',
    ' viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"',
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
    '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>',
    '<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
    '</svg>',
].join('');

function buildSectionCopyLink(headingId: string): string {
    return [
        `<button type="button" class="section-copy-link"`,
        ` data-heading-id="${headingId}"`,
        ` aria-label="Copy link to this section"`,
        ` title="Copy link to this section">`,
        LINK_ICON_SVG,
        `</button>`,
    ].join('');
}

export interface SkillMarkdownRendererOptions {
    /**
     * Emit the per-heading "copy link to this section" button. Off for deployed
     * artifacts, whose readers have no origin to copy a shareable link against.
     */
    sectionCopyLinks?: boolean;
}

/** Canonical markdown → HTML for skill guides; untrusted input — see AGENTS.md § Skill guide markdown and HTML safety. */
export class SkillMarkdownRenderer {
    private readonly sectionCopyLinks: boolean;

    constructor(
        private readonly treeId: string,
        options: SkillMarkdownRendererOptions = {},
    ) {
        this.sectionCopyLinks = options.sectionCopyLinks ?? true;
    }

    convertToSkillDescription(markdown: string, skillId: string): SkillDescription {
        const html = this.renderToHtml(markdown);
        const titleMatch = markdown.match(/^# (.+)$/m);
        const title = titleMatch ? titleMatch[1] : skillId;

        return {
            title,
            html,
            tableOfContents: extractMarkdownTableOfContents(markdown),
        };
    }

    private renderToHtml(markdown: string): string {
        const treeId = this.treeId;
        const sectionCopyLinks = this.sectionCopyLinks;
        const idCounts = new Map<string, number>();
        const md = new Marked();
        md.use({
            renderer: {
                heading(this: RendererThis, { tokens, depth, text }: Tokens.Heading) {
                    const id = allocateHeadingAnchorId(text, idCounts);
                    const inner = this.parser.parseInline(tokens);
                    const copyLink = sectionCopyLinks ? buildSectionCopyLink(id) : '';
                    return `<h${depth} id="${id}" class="skill-md-heading"><span class="skill-md-heading-inner">${inner}</span>${copyLink}</h${depth}>\n`;
                },
                image(token: Tokens.Image) {
                    const href = token.href;
                    const title = token.title;
                    const text = token.text;

                    const youtubeMatch = href?.match(/^video:yt:([a-zA-Z0-9_-]+)$/);
                    if (youtubeMatch) {
                        const videoId = youtubeMatch[1];
                        const safeTitle = escapeHtml(title || 'YouTube video');
                        return `<div class="video-embed">
                            <iframe 
                                width="480" 
                                height="360" 
                                src="https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&fs=1" 
                                frameborder="0" 
                                allowfullscreen
                                title="${safeTitle}"
                                loading="lazy"
                            ></iframe>
                        </div>`;
                    }

                    const safeHref = sanitizeUrl(href);
                    const safeAlt = escapeHtml(text);
                    return `<img src="${safeHref}" alt="${safeAlt}"${buildTitleAttr(title)} />`;
                },
                link(this: RendererThis, token: Tokens.Link) {
                    const href = token.href;
                    const leafMatch = href?.match(/^leaf:([A-Za-z0-9_-]+)(#.*)?$/);
                    if (leafMatch) {
                        const shortId = leafMatch[1];
                        const fragment = leafMatch[2] || '';
                        const url = `/tree/${treeId}/${shortId}${fragment}`;
                        const inner = this.parser.parseInline(token.tokens || []);
                        return `<a href="${escapeHtml(url)}" class="leaf-link" data-leaf-id="${escapeHtml(shortId)}"${buildTitleAttr(token.title)}>${inner}</a>`;
                    }
                    const inner = this.parser.parseInline(token.tokens || []);
                    const safeHref = sanitizeUrl(href);
                    return `<a href="${safeHref}"${buildTitleAttr(token.title)}>${inner}</a>`;
                },
                html({ text }: Tokens.HTML | Tokens.Tag) {
                    return escapeHtml(text);
                },
            }
        });
        return md.parse(markdown) as string;
    }
}
